import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { formatCurrency } from '@/lib/formatters';
import { DiretoriaSqlButton } from '@/components/Diretoria/DiretoriaSqlButton';
import { DiretoriaPlacaFinanceiroModal } from '@/components/Diretoria/DiretoriaPlacaFinanceiroModal';

export interface LocacoesAnalyticsChartsData {
  mensal?: Array<{ name: string; novas: number; canceladas: number }>;
  mensalAnos?: number[];
  mensalComparativo?: Array<{ mes: number; name: string } & Record<string, number | string>>;
  diario: Array<{ name: string; novas: number; canceladas: number }>;
  topModeloMensal?: Array<{
    name: string;
    marca: string;
    modelo: string;
    anoModelo?: string;
    quantidade: number;
  }>;
  topModeloAnos?: number[];
  topModeloComparativo?: Array<{ mes: number; name: string } & Record<string, number | string>>;
  topPlacas: Array<{
    placa: string;
    marca: string;
    modelo: string;
    anoModelo?: string;
    empresa?: string;
    idEmpresa?: string;
    idEquipamento?: number;
    quantidade: number;
    totalFaturado?: number;
    totalCusto?: number;
    diferenca?: number;
    diferencaPct?: number | null;
  }>;
  querySql?: string;
}

type TopPlacaRow = LocacoesAnalyticsChartsData['topPlacas'][number];
type PlacaSortKey =
  | 'placa'
  | 'modelo'
  | 'empresa'
  | 'quantidade'
  | 'totalFaturado'
  | 'totalCusto'
  | 'diferenca';

function normalizePlacaKeyClient(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

function formatModeloLabel(marca?: string, modelo?: string, anoModelo?: string): string {
  const base = [marca, modelo].filter(Boolean).join(' ').trim();
  if (!base) return '—';
  return anoModelo?.trim() ? `${base} · ${anoModelo.trim()}` : base;
}

function enrichTopPlacasWithCusto(
  rows: TopPlacaRow[],
  motosCusto?: Array<{ placa: string; custoAtual: number }>
): TopPlacaRow[] {
  if (!motosCusto?.length) return rows;
  const byPlaca = new Map<string, number>();
  for (const m of motosCusto) {
    const key = normalizePlacaKeyClient(m.placa);
    if (!key) continue;
    byPlaca.set(key, (byPlaca.get(key) ?? 0) + Math.abs(Number(m.custoAtual) || 0));
  }
  return rows.map((row) => {
    if ((row.totalCusto ?? 0) !== 0) return row;
    const fromErp = byPlaca.get(normalizePlacaKeyClient(row.placa));
    if (fromErp == null || fromErp === 0) return row;
    const totalFaturado = row.totalFaturado ?? 0;
    const diferenca = totalFaturado - fromErp;
    return {
      ...row,
      totalCusto: fromErp,
      diferenca,
      diferencaPct: totalFaturado !== 0 ? (diferenca / Math.abs(totalFaturado)) * 100 : null,
    };
  });
}

function compareTopPlaca(a: TopPlacaRow, b: TopPlacaRow, key: PlacaSortKey): number {
  switch (key) {
    case 'placa':
      return a.placa.localeCompare(b.placa, 'pt-BR');
    case 'modelo':
      return formatModeloLabel(a.marca, a.modelo, a.anoModelo).localeCompare(
        formatModeloLabel(b.marca, b.modelo, b.anoModelo),
        'pt-BR'
      );
    case 'empresa':
      return String(a.empresa ?? '').localeCompare(String(b.empresa ?? ''), 'pt-BR');
    case 'quantidade':
      return (a.quantidade ?? 0) - (b.quantidade ?? 0);
    case 'totalFaturado':
      return (a.totalFaturado ?? 0) - (b.totalFaturado ?? 0);
    case 'totalCusto':
      return (a.totalCusto ?? 0) - (b.totalCusto ?? 0);
    case 'diferenca':
      return (a.diferenca ?? 0) - (b.diferenca ?? 0);
    default:
      return 0;
  }
}

function SortableTh({
  label,
  sortKey,
  activeKey,
  dir,
  align = 'left',
  onSort,
}: {
  label: string;
  sortKey: PlacaSortKey;
  activeKey: PlacaSortKey;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right';
  onSort: (key: PlacaSortKey) => void;
}) {
  const active = activeKey === sortKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`py-2 pr-2 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${
          align === 'right' ? 'flex-row-reverse' : ''
        } ${active ? 'text-foreground' : 'text-muted-foreground'}`}
      >
        <span>{label}</span>
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
      </button>
    </th>
  );
}

/** Criadas (sólido) e canceladas (tracejado) por ano — tons distintos desde 2025. */
const YEAR_PALETTE: Array<{ criadas: string; canceladas: string }> = [
  { criadas: 'hsl(222 47% 18%)', canceladas: '#ca8a04' },
  { criadas: 'hsl(199 89% 38%)', canceladas: '#ea580c' },
  { criadas: 'hsl(160 60% 32%)', canceladas: '#a16207' },
  { criadas: 'hsl(280 45% 40%)', canceladas: '#c2410c' },
];

const COLOR_NOVAS = 'hsl(222 47% 18%)';
const COLOR_CANCEL = '#ca8a04';

export const DiretoriaLocacoesCharts: React.FC<{
  data: LocacoesAnalyticsChartsData | undefined;
  /** Fallback de custos (indicador custo-por-moto) quando a analytics não trouxer custo. */
  motosCusto?: Array<{ placa: string; custoAtual: number }>;
  companyId?: string;
}> = ({ data, motosCusto, companyId }) => {
  const anos = data?.mensalAnos ?? [];
  const mensalComparativo = data?.mensalComparativo ?? [];
  const hasMensalComparativo = mensalComparativo.length === 12 && anos.length > 0;
  const sql = data?.querySql;

  const topModeloAnos = data?.topModeloAnos ?? anos;
  const topModeloComparativo = data?.topModeloComparativo ?? [];
  const hasTopModeloComparativo = topModeloComparativo.length === 12 && topModeloAnos.length > 0;

  const [sortKey, setSortKey] = useState<PlacaSortKey>('quantidade');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [placaModal, setPlacaModal] = useState<{
    placa: string;
    idEquipamento?: number;
    modeloLabel?: string;
    empresa?: string;
  } | null>(null);

  const topPlacasSorted = useMemo(() => {
    const enriched = enrichTopPlacasWithCusto(data?.topPlacas ?? [], motosCusto);
    const sorted = [...enriched].sort((a, b) => compareTopPlaca(a, b, sortKey));
    if (sortDir === 'desc') sorted.reverse();
    return sorted;
  }, [data?.topPlacas, motosCusto, sortKey, sortDir]);

  const onSort = (key: PlacaSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'placa' || key === 'modelo' || key === 'empresa' ? 'asc' : 'desc');
  };

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Locações mês a mês</CardTitle>
              <p className="text-xs text-muted-foreground">
                Jan–Dez · séries por ano desde 2025 · criadas = início no mês · canceladas =
                cancelamento no mês (empresa selecionada)
              </p>
            </div>
            <DiretoriaSqlButton sql={sql} source="cobranca" title="Locações mês a mês — consulta SQL" />
          </div>
        </CardHeader>
        <CardContent className="h-72 pt-0">
          {hasMensalComparativo ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mensalComparativo}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {anos.map((ano, i) => {
                  const palette = YEAR_PALETTE[i % YEAR_PALETTE.length]!;
                  return (
                    <React.Fragment key={ano}>
                      <Line
                        type="monotone"
                        dataKey={`novas_${ano}`}
                        name={`Criadas ${ano}`}
                        stroke={palette.criadas}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                      <Line
                        type="monotone"
                        dataKey={`canceladas_${ano}`}
                        name={`Canceladas ${ano}`}
                        stroke={palette.canceladas}
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={{ r: 3 }}
                      />
                    </React.Fragment>
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Sem dados no período
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Locações dia a dia</CardTitle>
              <p className="text-xs text-muted-foreground">Criadas e cancelamentos no intervalo</p>
            </div>
            <DiretoriaSqlButton sql={sql} source="cobranca" title="Locações dia a dia — consulta SQL" />
          </div>
        </CardHeader>
        <CardContent className="h-64 pt-0">
          {(data?.diario?.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data!.diario}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="novas" name="Criadas" stroke={COLOR_NOVAS} strokeWidth={2} dot={false} />
                <Line
                  type="monotone"
                  dataKey="canceladas"
                  name="Canceladas"
                  stroke={COLOR_CANCEL}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Sem dados no período
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Modelo que mais alugou (mês a mês)</CardTitle>
              <p className="text-xs text-muted-foreground">
                Jan–Dez · quantidade do top modelo por ano desde 2025
              </p>
            </div>
            <DiretoriaSqlButton
              sql={sql}
              source="cobranca"
              title="Modelo que mais alugou — consulta SQL"
            />
          </div>
        </CardHeader>
        <CardContent className="h-72 pt-0">
          {hasTopModeloComparativo ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topModeloComparativo} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v: number, name: string, item) => {
                    const ano = String(name);
                    const modelo = String(
                      (item?.payload as Record<string, unknown>)?.[`modelo_${ano}`] ?? ''
                    ).trim();
                    return [modelo ? `${v} · ${modelo}` : String(v), `Top ${ano}`];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {topModeloAnos.map((ano, i) => {
                  const palette = YEAR_PALETTE[i % YEAR_PALETTE.length]!;
                  return (
                    <Bar
                      key={ano}
                      dataKey={`qtd_${ano}`}
                      name={`${ano}`}
                      fill={palette.criadas}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={28}
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Sem dados no período
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80 xl:col-span-2">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Placas com mais locações</CardTitle>
              <p className="text-xs text-muted-foreground">
                Histórico geral (exclui ERRO EM/DE CONTRATO) · faturado = títulos liquidados
                (FECHADO, vencimento &lt; hoje) · custo = OS + contas a pagar (ERP) · clique na placa
                para ver receitas e despesas
              </p>
            </div>
            <DiretoriaSqlButton
              sql={sql}
              source="cobranca"
              title="Placas com mais locações — consulta SQL"
            />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {topPlacasSorted.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="py-2 pr-2 text-left font-medium">#</th>
                    <SortableTh label="Placa" sortKey="placa" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                    <SortableTh label="Modelo" sortKey="modelo" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                    <SortableTh label="Empresa" sortKey="empresa" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                    <SortableTh
                      label="Locações"
                      sortKey="quantidade"
                      activeKey={sortKey}
                      dir={sortDir}
                      align="right"
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Faturado"
                      sortKey="totalFaturado"
                      activeKey={sortKey}
                      dir={sortDir}
                      align="right"
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Custos"
                      sortKey="totalCusto"
                      activeKey={sortKey}
                      dir={sortDir}
                      align="right"
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Diferença"
                      sortKey="diferenca"
                      activeKey={sortKey}
                      dir={sortDir}
                      align="right"
                      onSort={onSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {topPlacasSorted.map((row, i) => {
                    const diferenca = row.diferenca ?? 0;
                    const pct = row.diferencaPct;
                    return (
                      <tr key={`${row.placa}-${row.empresa ?? ''}-${i}`} className="border-b border-border/60">
                        <td className="py-2 pr-2 tabular-nums text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-2 font-medium">
                          <button
                            type="button"
                            className="text-left font-semibold text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() =>
                              setPlacaModal({
                                placa: row.placa,
                                idEquipamento: row.idEquipamento,
                                modeloLabel: formatModeloLabel(row.marca, row.modelo, row.anoModelo),
                                empresa: row.empresa,
                              })
                            }
                          >
                            {row.placa}
                          </button>
                        </td>
                        <td className="py-2 pr-2 text-muted-foreground">
                          {formatModeloLabel(row.marca, row.modelo, row.anoModelo)}
                        </td>
                        <td className="py-2 pr-2 text-muted-foreground">{row.empresa || '—'}</td>
                        <td className="py-2 pr-2 text-right tabular-nums font-semibold">{row.quantidade}</td>
                        <td className="py-2 pr-2 text-right tabular-nums">
                          {formatCurrency(row.totalFaturado ?? 0)}
                        </td>
                        <td className="py-2 pr-2 text-right tabular-nums">
                          {formatCurrency(row.totalCusto ?? 0)}
                        </td>
                        <td className="py-2 text-right tabular-nums">
                          <span
                            className={
                              diferenca >= 0 ? 'text-emerald-700 font-medium' : 'text-red-700 font-medium'
                            }
                          >
                            {formatCurrency(diferenca)}
                          </span>
                          {pct != null ? (
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({pct >= 0 ? '+' : ''}
                              {pct.toFixed(1).replace('.', ',')}%)
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">Sem dados no período</p>
          )}
        </CardContent>
      </Card>

      <DiretoriaPlacaFinanceiroModal
        open={Boolean(placaModal)}
        onClose={() => setPlacaModal(null)}
        placa={placaModal?.placa ?? ''}
        idEquipamento={placaModal?.idEquipamento}
        companyId={companyId}
        modeloLabel={placaModal?.modeloLabel}
        empresa={placaModal?.empresa}
      />
    </div>
  );
};
